import React from "react";
import { IconType } from "react-icons";
import {
  MdOutlineDoneAll,
  MdOutlineVisibility,
  MdOutlineSchedule,
  MdErrorOutline,
} from "react-icons/md";
import { DashboardData } from "../../types/dashboardType";

interface Props {
  data: DashboardData | null;
  loading: boolean;
}

// Share of all sent messages in each state. Bars are separate (not stacked)
// because the API doesn't say whether "read" is counted inside "delivered".
export default function MessageStatusCard({ data, loading }: Props) {
  const total = data?.totalSent ?? 0;

  const rows: { label: string; value: number; Icon: IconType; color: string; iconClass: string }[] = [
    { label: "Delivered", value: data?.delivered ?? 0, Icon: MdOutlineDoneAll, color: "#2a78d6", iconClass: "text-slate-500" },
    { label: "Read", value: data?.read ?? 0, Icon: MdOutlineVisibility, color: "#2a78d6", iconClass: "text-slate-500" },
    { label: "Pending", value: data?.pending ?? 0, Icon: MdOutlineSchedule, color: "#fab219", iconClass: "text-amber-600" },
    { label: "Failed", value: data?.failed ?? 0, Icon: MdErrorOutline, color: "#d03b3b", iconClass: "text-red-600" },
  ];

  return (
    <div className="bg-white rounded-3xl border border-app-line shadow-card p-5 sm:p-6">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[15px] font-semibold text-app-ink">Message status</h3>
        <span className="text-[12px] text-app-muted tabular-nums">
          of {total.toLocaleString()} sent
        </span>
      </div>

      <ul className="mt-5 space-y-4">
        {rows.map(({ label, value, Icon, color, iconClass }) => {
          const pct = total > 0 ? (value / total) * 100 : 0;
          return (
            <li key={label}>
              <div className="flex items-center justify-between gap-3 text-[13px]">
                <span className="flex items-center gap-2 text-slate-700">
                  <Icon className={`text-[16px] ${iconClass}`} aria-hidden />
                  {label}
                </span>
                {loading ? (
                  <span className="h-3 w-16 rounded-full bg-app-bg animate-pulse" />
                ) : (
                  <span className="tabular-nums text-app-ink font-medium">
                    {value.toLocaleString()}
                    <span className="ml-1.5 text-app-muted font-normal">{pct.toFixed(pct > 0 && pct < 10 ? 1 : 0)}%</span>
                  </span>
                )}
              </div>
              <div className="mt-2 h-2 rounded-full bg-app-bg overflow-hidden">
                <div
                  className="h-full rounded-full transition-[width] duration-700"
                  style={{ width: loading ? 0 : `${Math.min(pct, 100)}%`, backgroundColor: color }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
